import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { IFilterDefinition, IListingConfig, IListingSetup, Listing } from '@df/catalog-ui/listing/listing';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { Category } from '@df/catalog/category/category';
import { IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { IModelDataV2, Model } from '@df/catalog/model';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { ICmsPageDecorator } from '../../page/icms-page.decorator';
import { NCmsComponentListing } from './cms-component-listing.interface';
import { isCustomRule } from './cms-component-listing.utils';

@Directive()
export abstract class CmsComponentListingAbstract extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsComponentListing.IData;

    /**
     * Listing model
     */
    listing: Listing | ProductListing | undefined;

    /**
     * Listing model subscription
     */
    listingSubscription?: Subscription;

    protected app = inject(App);
    protected iCmsDataChangeService = inject(ICmsDataChangeService);
    private catalogUiConfig = inject(NCatalogUi.OPTIONS);

    override ngOnInit(): void {
        super.ngOnInit();

        this.prepare();

        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (
                    event.changeType === NICmsDataChange.EChangeType.change &&
                    this.cmsObservedData.some(path => event.valuePath.includes(path))
                ) {
                    this.prepare();
                }
            })
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.unsubscribe();
    }

    get cmsObservedData(): string[] {
        return ['type', 'matching', 'pageType', 'group', 'rule', 'sort', 'verbosity'];
    }

    get listingAsProducts(): ProductListing {
        return <ProductListing>this.listing;
    }

    get listingAsPages(): Listing<ICmsPageDecorator<IPageDataV3>> {
        return this.listing as unknown as Listing<ICmsPageDecorator<IPageDataV3>>;
    }

    get listingAsCategories(): Listing<Category> {
        return this.listing as unknown as Listing<Category>;
    }

    getFilters(): (string | IFilterDefinition)[] {
        return [];
    }

    onListingChange(): void {
        this.detectChanges();
    }

    trackByProductId(index: number, model: Product) {
        return model.id;
    }

    trackByPageId(index: number, model: ICmsPageDecorator) {
        return model.id;
    }

    trackByCategoryId(index: number, model: Category) {
        return model.id;
    }

    /**
     * Creates new listing on model change
     */
    protected prepare(): Promise<void | Model<IModelDataV2>[]> | void {
        try {
            this.unsubscribe();

            const type = this.component?.type;
            const pageType = this.component?.pageType;
            const group = this.component?.group;
            const rule = this.component?.rule;

            let filter = {};

            if (type) {
                const promise: Promise<Model<IModelDataV2>[]> = new Promise((resolve, reject) => {
                    if (isCustomRule(this.component)) {
                        if (rule) {
                            filter = JSON.parse(rule);
                        } else {
                            reject('Missing custom rule');
                        }
                    } else {
                        switch (this.component.matching) {
                            case 'pageType':
                                if (pageType) {
                                    filter = {
                                        type,
                                        pageType
                                    };
                                } else {
                                    reject('Missing page type');
                                    return;
                                }

                                break;

                            case 'group':
                                if (group) {
                                    filter = {
                                        type,
                                        groups: {
                                            $in: [group]
                                        }
                                    };
                                } else {
                                    reject('Missing group id');
                                    return;
                                }

                                break;

                            default:
                                reject('Invalid matching type');
                                return;
                        }
                    }

                    this.app.resources[type]
                        .find({
                            filter,
                            verbosity: this.component.verbosity || 2,
                            sort: this.component.sort && JSON.parse(this.component.sort),
                            limit: this.component.limit && parseInt(this.component.limit, 10)
                        })
                        .then((models: Model<IModelDataV2>[]) => resolve(models))
                        .catch(e => reject(e));
                });

                promise
                    .then((models: Model<IModelDataV2, number>[]) => {
                        const ops = this.getListingOptions();
                        const ids = this.getListingModelIds(models);

                        this.listing =
                            this.component.type === 'product'
                                ? new ProductListing(ops, ids, this.getListingSetup() as IListingSetup<Product>)
                                : new Listing(ops, ids, this.getListingSetup());
                        this.listingSubscription = this.listing?.subscribe(() => this.onListingChange());
                        this.onListingCreated();
                    })
                    .catch(e => {
                        this.app.logger.error(e);
                        this.listing = undefined;
                        this.onListingCreated();
                    });

                return promise;
            } else {
                this.listing = undefined;
                this.onListingCreated();
            }
        } catch (err) {
            // skip silently - we are fine with errors while editing
        }
    }

    protected getListingSetup(): IListingSetup {
        return {
            filtersDefinitions: this.getFilters()
        };
    }

    protected getListingModelIds(models: Model<IModelDataV2, number>[]): number[] {
        return models.map(i => i.id);
    }

    protected getListingOptions(): IListingConfig {
        return {
            initialStepsVisible: this.component.initialStepsVisible,
            type: this.component.type,
            source: this.component,
            infScrollStep: this.component.infScrollStep || this.catalogUiConfig.listing.cms.infScrollStep,
            storage: true
        };
    }

    protected onListingCreated(): void {
        this.detectChanges();
    }

    /**
     * Unsubscribes listing changes if subscription exists
     */
    protected unsubscribe() {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
        }
    }
}
